import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
/* ============================================================================
   world.js — the exterior for AGENTSEE (three 0.170)

   The floor is one level of a tower, thirty-odd storeys up, at dusk, over a
   city. Everything out there is cheap on purpose: the floor plate and fifteen
   skinned characters own the frame budget, so the world buys its scale with
   instancing, one shader sphere and a handful of fogged quads.

   THE INVARIANT that makes the far plane a non-issue: every layer except the
   city follows the camera in XZ (sky r=100, cloud domes r=260, haze r=320,
   streets 520 snapped to its own block period), so nothing but the towers can
   ever reach the frustum's far face. The towers stop at r=290 and their haze
   ramp saturates to 100% by ~345 — a tower clipped at the host's far=400 is
   already solid horizon colour, so clipping is cosmetically moot. Only raise
   opts.far if the host raises controls.maxDistance past ~120.

   DRAW CALLS — 14 with the ceiling off (default), 16 with it on:
     sky 1 · cloud domes 2 · haze band 1 · towers 2 (near + far InstancedMesh)
     streets 1 + traffic 2 · life 1 (aircraft nav lights + roof beacons, one
     Points) · shell: fascia+rails merged 1, columns 1 (Instanced), glass
     merged 1, mullions 1 (Instanced) · ceiling slab 1 + downlights 1 (off).
     quality:'medium' → 12 · quality:'low' → 9.

   TRIANGLES — ~7.6k at quality 'high':
     sky 530 · clouds 1536 · haze 576 · towers 96×12 + 168×12 = 3168
     streets/traffic 6 · shell 144 + 26×12 + 8 + 112×12 = 1808 · ceiling 112.
   That is noise. The real cost is FILL: sky + 2 cloud domes + haze + streets
   + 2 traffic quads is up to 7 near-fullscreen blends into a HalfFloat target
   before UnrealBloom's mip chain. That is what the quality ladder cuts, in
   that order — never the tower count, which is where the look lives and none
   of the cost. Cloud domes at grazing angles are the first thing to go.

   COLOUR — the host renders through buildPost's EffectComposer, so materials
   never tone map (WebGLPrograms.js:164-174 gates TONE_MAPPING on
   currentRenderTarget === null) and OutputPass does ACES at the end.
   Everything here writes LINEAR. Window emissive runs 0.75 (dusk) → 1.90
   (night) against the host's bloom toe of 0.86; the default k=0.34 lands at
   1.14, just over it, so the skyline halates about as much as the room's own
   practicals do. That is the whole intent — retune winInt, never bloom strength.

   FOG — the host owns scene.fog. It is a FogExp2 whose density applyGrade()
   rewrites EVERY FRAME from GRADE.fog (0.0052 plan / 0.0135 walk), so nothing
   here may set it: at 0.0052 the city would be 90% gone by 250. Instead every
   distant layer runs fog:false and the towers carry their own aerial ramp
   (uHz0/uHz1), which is why the office can stay crisp while the skyline
   hazes. { fog:true } opts into recolouring scene.fog.color to match the
   graded horizon — colour only, never density, never a new Fog object.

   WHAT IT TOUCHES OUTSIDE ITS OWN GROUP — all restored by dispose():
     · scene.fog.color → only with { fog:true }. Default: hands off.
     · camera.far      → only if opts.far is set. Default null: hands off.
   It never touches scene.background (the sky draws over it with depthTest
   off), scene.environment, toneMappingExposure, the lights, the GRADE system,
   or anything main.js built.

   Sky.js AND SkyMesh.js are both present in this three build and both were
   rejected: Preetham is a daylight model — it cannot grade to night, and at
   the sub-horizon sun elevation this scene wants it blows the horizon out
   under ACES. The shader sphere below costs ~530 triangles and one program,
   and grades dusk→night off a single uniform.
   ============================================================================ */

/* ---------- constants: one unit ≈ one metre, the floor plate is 55m ---------- */
const GROUND_Y  = -62;        // streets, ~18 storeys down — we are high up
const SLAB_TOP  = 0;          // main.js p2 top
const SLAB_BOT  = -0.92;      // main.js p1 bottom
const CEIL_Y    = 3.4;        // floor-to-slab-soffit
const GLAZE     = { x0:-24.2, x1:31.2, z0:-13.7, z1:13.7 };   // flush to p2 (x -24.3..31.3, z ±13.8)
const FASCIA    = { x0:-26.25, x1:33.25, z0:-15.25, z1:15.25 };// just outboard of p1 (x -26..33, z ±15)
const UNDER     = 0.15;       // main.js: below = camera.position.y < 0.15
const WIN_COLS  = 16, WIN_ROWS = 32;              // window cells per texture tile
const BAY_W     = 3.2, BAY_H = 3.6;               // metres per window bay / floor-to-floor
const TILE_W    = WIN_COLS * BAY_W;               // 51.2m of facade per tile
const TILE_H    = WIN_ROWS * BAY_H;               // 115.2m of facade per tile
/* Azimuth is copied exactly from the host key at (-40, 24, -34) — the sky glow has
   to sit where the shadows say the sun is. Elevation is dropped from 24° to 7° so
   the sodium band and the god-ray core hug the horizon, which is what reads as dusk.
   Override with opts.sun if the host ever swings its key. */
const SUN_DEF   = new THREE.Vector3(-40, 7, -34).normalize();

const QUALITY = {
  high:   { near:96, far:168, clouds:2, traffic:2, haze:true,  ground:true },
  medium: { near:64, far:112, clouds:1, traffic:1, haze:true,  ground:true },
  low:    { near:44, far:72,  clouds:0, traffic:0, haze:false, ground:true },
};

/* dusk (k=0) → night (k=1). Every pair is lerped in setTime with no allocation. */
const GRADE = {
  zenith:   ['#16223f', '#05070e'],
  midsky:   ['#3a3550', '#0b0d18'],
  horizon:  ['#6b4a45', '#171622'],
  sodium:   ['#ff7a2e', '#c2521a'],
  nadir:    ['#241f28', '#07080c'],
  towerTop: ['#2a2b38', '#14151f'],
  towerBot: ['#1c1d27', '#0c0d14'],
  cloud:    ['#6b5a63', '#191d2c'],
  hazeWarm: ['#ff8a45', '#7a3f22'],
  hazeCool: ['#4a5a86', '#161b2e'],
  street:   ['#8c8478', '#4a4640'],
};
const SCALAR = {                       // [dusk, night]
  sunGlow:   [1.00, 0.10],
  winLit:    [0.30, 0.86],             // fraction of cells alight
  winInt:    [0.75, 1.90],             // linear; the host bloom toe is 0.86, so the default k=0.34 lands at 1.14 —
                                       // just over, which is the point: the skyline halates, the mid-tones do not
  hazeOp:    [0.55, 0.26],
  cloudOp:   [0.34, 0.17],
  trafficOp: [0.30, 0.85],
};

/* ---------- seeded PRNG — the skyline is the same every reload ---------- */
function mulberry32(a){
  return function(){
    a |= 0; a = a + 0x6D2B79F5 | 0;
    let t = Math.imul(a ^ a >>> 15, 1 | a);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

/* ---------- procedural canvases ---------- */
/* A tile of lit and unlit windows. Sampled as the window COLOUR; the lit/unlit
   decision is re-rolled per cell in the shader so it can vary per instance and
   flicker over time — the texture only carries grain, tint and the mullion cut. */
function windowTexture(rnd){
  const cw = 16, c = document.createElement('canvas');
  c.width = WIN_COLS * cw; c.height = WIN_ROWS * cw;
  const g = c.getContext('2d');
  g.fillStyle = '#0a0a0d'; g.fillRect(0, 0, c.width, c.height);
  const TINT = ['#ffd9a4', '#ffc98a', '#ffe6c2', '#dfe6f0', '#ffb877', '#f2ecd8', '#c9d8ea'];
  for(let ry = 0; ry < WIN_ROWS; ry++){
    for(let cx = 0; cx < WIN_COLS; cx++){
      const t = TINT[(rnd() * TINT.length) | 0];
      g.fillStyle = t;
      g.globalAlpha = 0.45 + rnd() * 0.55;                      // per-pane brightness grain
      g.fillRect(cx * cw + 2, ry * cw + 3, cw - 4, cw - 6);     // the pane, inset from its mullion
      if(rnd() > 0.72){                                         // a blind half-drawn, or a lit desk at the glass
        g.globalAlpha *= 0.35;
        g.fillStyle = '#0d0d11';
        g.fillRect(cx * cw + 2, ry * cw + 3, cw - 4, (cw - 6) * (0.3 + rnd() * 0.45));
      }
    }
  }
  g.globalAlpha = 1;
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}

/* City blocks seen from above: asphalt, block masses, street lines, sodium lamps. */
function streetTexture(rnd){
  const S = 512, BLK = 8, cell = S / BLK;
  const c = document.createElement('canvas'); c.width = c.height = S;
  const g = c.getContext('2d');
  g.fillStyle = '#0b0b0e'; g.fillRect(0, 0, S, S);
  for(let bx = 0; bx < BLK; bx++) for(let bz = 0; bz < BLK; bz++){
    const r = rnd();
    g.fillStyle = r > 0.9 ? '#0d150f' : (r > 0.55 ? '#191921' : '#141419');   // park / mid / dark block
    g.fillRect(bx * cell + 5, bz * cell + 5, cell - 10, cell - 10);
    if(r < 0.5){                                                              // rooftop plant, faintly catching light
      g.fillStyle = '#22222b';
      for(let i = 0; i < 3; i++)
        g.fillRect(bx * cell + 10 + rnd() * (cell - 30), bz * cell + 10 + rnd() * (cell - 30), 6 + rnd() * 12, 6 + rnd() * 12);
    }
  }
  g.strokeStyle = '#141317'; g.lineWidth = 9;                                 // the roadway itself
  for(let i = 0; i <= BLK; i++){
    g.beginPath(); g.moveTo(i * cell, 0); g.lineTo(i * cell, S); g.stroke();
    g.beginPath(); g.moveTo(0, i * cell); g.lineTo(S, i * cell); g.stroke();
  }
  g.fillStyle = '#ffb066';                                                    // sodium street lamps
  for(let i = 0; i <= BLK; i++) for(let s = 0; s < S; s += 21){
    g.globalAlpha = 0.35 + rnd() * 0.4;
    g.fillRect(i * cell - 1, s, 2, 2); g.fillRect(s, i * cell - 1, 2, 2);
  }
  g.globalAlpha = 1;
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return t;
}

/* Headlight/taillight dashes on one axis only, so two scrolled copies read as
   traffic running both ways on a grid instead of the whole city sliding. */
function trafficTexture(rnd, axis){
  const S = 512, BLK = 8, cell = S / BLK;
  const c = document.createElement('canvas'); c.width = c.height = S;
  const g = c.getContext('2d');
  g.fillStyle = '#000'; g.fillRect(0, 0, S, S);
  for(let i = 0; i <= BLK; i++){
    const lane = i * cell;
    for(let s = 0; s < S; s += 7){
      if(rnd() > 0.42) continue;
      g.fillStyle = rnd() > 0.5 ? '#fff0d0' : '#ff4a30';      // heading away / coming at you
      g.globalAlpha = 0.5 + rnd() * 0.5;
      if(axis === 'x') g.fillRect(s, lane - 2.5, 3 + rnd() * 4, 2.2);
      else             g.fillRect(lane - 2.5, s, 2.2, 3 + rnd() * 4);
    }
  }
  g.globalAlpha = 1;
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/* Soft banded cloud, alpha only — the dome material supplies the colour. */
function cloudTexture(rnd){
  const S = 512, c = document.createElement('canvas'); c.width = c.height = S;
  const g = c.getContext('2d');
  g.fillStyle = '#000'; g.fillRect(0, 0, S, S);
  g.globalCompositeOperation = 'lighter';
  for(let i = 0; i < 190; i++){
    const x = rnd() * S, y = rnd() * S, r = 22 + rnd() * 96;
    const gr = g.createRadialGradient(x, y, 0, x, y, r);
    gr.addColorStop(0, 'rgba(255,255,255,' + (0.1 + rnd() * 0.24).toFixed(3) + ')');
    gr.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = gr;
    g.save(); g.translate(x, y); g.scale(1 + rnd() * 2.6, 0.34 + rnd() * 0.3);   // stretched: high cloud is banded
    g.beginPath(); g.arc(0, 0, r, 0, 7); g.fill(); g.restore();
  }
  g.globalCompositeOperation = 'source-over';
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}

/* A round falloff for the nav lights and roof beacons. */
function dotTexture(){
  const S = 64, c = document.createElement('canvas'); c.width = c.height = S;
  const g = c.getContext('2d');
  const gr = g.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
  gr.addColorStop(0, 'rgba(255,255,255,1)');
  gr.addColorStop(0.28, 'rgba(255,255,255,0.72)');
  gr.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = gr; g.fillRect(0, 0, S, S);
  return new THREE.CanvasTexture(c);
}

/* ---------- shader source ---------- */
const HASH = /* glsl */`
  float h11(float p){ p = fract(p * 0.1031); p *= p + 33.33; p *= p + p; return fract(p); }
  float h21(vec2 p){ vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
    q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
`;

const SKY_VERT = /* glsl */`
  varying vec3 vDir;
  void main(){
    vDir = normalize(position);                       // sphere is only translated, so local dir == world dir
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const SKY_FRAG = /* glsl */`
  uniform vec3  uZenith, uMid, uHorizon, uSodium, uNadir, uSunDir;
  uniform float uGlow;
  varying vec3  vDir;
  ${HASH}
  void main(){
    vec3 d = normalize(vDir);
    float up = d.y;
    /* above the line: horizon → mid → zenith, weighted so the warm band stays thin */
    vec3 col = mix(uHorizon, uMid, smoothstep(0.0, 0.24, up));
    col = mix(col, uZenith, smoothstep(0.14, 0.78, up));
    col = mix(col, uNadir, smoothstep(0.0, -0.30, up));                 // below: the city's own dark
    col += uSodium * exp(-abs(up) * 13.0) * 0.55 * uGlow;               // the sodium haze sitting on the horizon
    float sd = max(dot(d, uSunDir), 0.0);
    col += uSodium * pow(sd, 3.2) * 0.30 * uGlow;                       // broad forward scatter
    col += uSodium * pow(sd, 46.0) * 1.15 * uGlow;                      // the cheap god-ray core
    col *= 1.0 - smoothstep(-0.02, -0.42, up) * 0.55;                   // ground half is darker than the air
    col += (h21(gl_FragCoord.xy) - 0.5) * 0.004;                        // dither: 8-bit gradients band badly
    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
`;

/* Injected into MeshBasicMaterial. No lights: 16 lights across a skyline is
   pure waste and the dusk grade is wanted under uniform control anyway. The
   raw `normal` attribute is used rather than objectNormal because meshbasic
   gates <beginnormal_vertex> behind USE_ENVMAP — for an axis-scaled, Y-rotated
   box, |normal.y| is exactly 1 on a roof and 0 on a wall. */
const TOWER_PARS_V = /* glsl */`
  attribute vec4  aUv;      // xy = per-instance uv offset, zw = per-instance uv repeat
  attribute float aSeed;
  varying vec4  vAUv;
  varying float vSeed, vUp, vY, vDepth;
  varying vec2  vWUv;
`;
const TOWER_PARS_F = /* glsl */`
  uniform sampler2D uWin;
  uniform vec3  uTop, uBot, uHaze;
  uniform float uTime, uLit, uWinI, uHz0, uHz1, uDim;
  varying vec4  vAUv;
  varying float vSeed, vUp, vY, vDepth;
  varying vec2  vWUv;
  ${HASH}
`;
const TOWER_BODY = /* glsl */`
  vec2  tuv  = vWUv * vAUv.zw + vAUv.xy;                       // no fract(): RepeatWrapping keeps derivatives clean
  vec2  cell = floor(tuv * vec2(${WIN_COLS}.0, ${WIN_ROWS}.0));
  float lit  = step(h21(cell + vSeed * 37.0), uLit);           // the evening's occupancy
  float ph   = h21(cell.yx + vSeed * 11.0 + 5.0);
  float flick= step(0.88, h21(cell + vSeed * 5.0 + 91.0));     // ~12% of windows ever switch
  lit *= mix(1.0, step(0.45, fract(uTime * (0.03 + ph * 0.09) + ph)), flick);
  float roof = smoothstep(0.55, 0.9, vUp);
  lit *= 1.0 - roof;                                           // no windows in a rooftop
  vec3  body = mix(uBot, uTop, vY) * (0.72 + 0.56 * h11(vSeed));
  body *= mix(1.0, 0.5, roof);
  vec3  city = body + texture2D(uWin, tuv).rgb * (lit * uWinI * (0.55 + 0.9 * ph));
  /* The city's own aerial perspective, steep and independent of scene.fog so the
     office can stay crisp. Haze is a property of the air, so the ramp is identical
     for both rings and depends only on depth; uDim is a small constant bias that
     keeps the far ring from ever reading crisp. Saturates by ~345, which is what
     retires the far plane — see the header invariant. */
  float hz = clamp(smoothstep(uHz0, uHz1, vDepth) + uDim, 0.0, 1.0);
  outgoingLight = mix(city, uHaze, hz);
`;

/* ============================================================================
   buildWorld
   ==========================================================================*/
/**
 * The exterior world for the AGENTSEE floor: sky, city, the building we are in,
 * atmosphere, aircraft and streets. Everything lands in one group.
 *
 * @param {THREE.Scene} scene
 * @param {object} [opts]
 *   quality  'high'|'medium'|'low'   default 'high'
 *   time     0..1 dusk→night         default 0.34
 *   seed     int                     default 20260830
 *   fog      bool                    default false — true recolours scene.fog.color only
 *   sun      Vector3|[x,y,z]         default (-40,7,-34) — copy the host key's azimuth
 *   ceiling  bool                    default false — the dollhouse view needs it off
 *   ground   bool                    default true  — streets far below
 *   clouds   bool                    default true
 *   aircraft bool                    default true
 *   camera   THREE.PerspectiveCamera default null  — only needed with opts.far
 *   far      number|null             default null  — only if maxDistance goes past ~120
 * @returns {{group:THREE.Group, update:function, setTime:function,
 *            setCeiling:function, params:object, dispose:function}}
 */
export function buildWorld(scene, opts = {}){
  const Q      = QUALITY[opts.quality] || QUALITY.high;
  const rnd    = mulberry32(opts.seed !== undefined ? opts.seed : 20260830);
  const SUN    = opts.sun ? new THREE.Vector3().fromArray(opts.sun.toArray ? opts.sun.toArray() : opts.sun).normalize()
                          : SUN_DEF.clone();
  const group  = new THREE.Group();
  group.name = 'world';
  scene.add(group);

  const tex = [], geo = [], mat = [];                      // everything dispose() has to hand back
  const keep = t => (tex.push(t), t);
  const keepG = g => (geo.push(g), g);
  const keepM = m => (mat.push(m), m);

  /* ---------- 1 · sky ---------- */
  const skyU = {
    uZenith:{ value:new THREE.Color() }, uMid:{ value:new THREE.Color() },
    uHorizon:{ value:new THREE.Color() }, uSodium:{ value:new THREE.Color() },
    uNadir:{ value:new THREE.Color() }, uSunDir:{ value:SUN.clone() },
    uGlow:{ value:1 },
  };
  const skyMat = keepM(new THREE.ShaderMaterial({
    uniforms: skyU, vertexShader: SKY_VERT, fragmentShader: SKY_FRAG,
    side: THREE.BackSide, depthWrite: false, depthTest: false, fog: false,
  }));
  const sky = new THREE.Mesh(keepG(new THREE.SphereGeometry(100, 24, 12)), skyMat);
  sky.renderOrder = -10000;          // drawn first, depth-blind: nothing can clip it, nothing can hide behind it
  sky.frustumCulled = false;
  sky.matrixAutoUpdate = true;
  group.add(sky);

  /* ---------- 2+3 · the city, two InstancedMesh calls ---------- */
  const winTex = keep(windowTexture(rnd));
  const towerGeo = keepG(new THREE.BoxGeometry(1, 1, 1));

  function towerMat(dim, hz0, hz1){
    const m = keepM(new THREE.MeshBasicMaterial({ color:0xffffff, fog:false, toneMapped:true }));
    m.onBeforeCompile = shader => {
      shader.uniforms.uWin  = { value: winTex };
      shader.uniforms.uTop  = { value: new THREE.Color() };
      shader.uniforms.uBot  = { value: new THREE.Color() };
      shader.uniforms.uHaze = { value: new THREE.Color() };
      shader.uniforms.uTime = { value: 0 };
      shader.uniforms.uLit  = { value: 0.3 };
      shader.uniforms.uWinI = { value: 1.2 };
      shader.uniforms.uHz0  = { value: hz0 };
      shader.uniforms.uHz1  = { value: hz1 };
      shader.uniforms.uDim  = { value: dim };
      shader.vertexShader = shader.vertexShader
        .replace('void main() {', TOWER_PARS_V + '\nvoid main() {')
        .replace('#include <begin_vertex>',
          '#include <begin_vertex>\n vAUv = aUv; vSeed = aSeed; vWUv = uv;\n' +
          ' vUp = abs(normal.y); vY = position.y + 0.5;')
        .replace('#include <project_vertex>',
          '#include <project_vertex>\n vDepth = -mvPosition.z;');
      shader.fragmentShader = shader.fragmentShader
        .replace('void main() {', TOWER_PARS_F + '\nvoid main() {')
        .replace('#include <opaque_fragment>', TOWER_BODY + '\n#include <opaque_fragment>');
      m.userData.u = shader.uniforms;   // onBeforeCompile uniforms are untracked — hold the reference
      applyTowerGrade();                // and grade them now: this fires on first render, after setTime already ran
    };
    return m;
  }
  const nearMat = towerMat(0.00, 100, 345);
  const farMat  = towerMat(0.12, 100, 345);   // same air, a touch hazier at the base

  /* Open sides are +X (east, stub wall) and +Z (south, stub wall); -X and -Z are
     the tall solid walls, so the interior can only see out east and south. Pull
     the visible quadrants closer and taller — that is where the money is. */
  /* Instanced attributes live on the geometry, so each ring owns a clone of the box. */
  function buildRing(cfg){
    const { n: count, r0: rMin, r1: rMax, h0: hMin, h1: hMax, w0: wMin, w1: wMax } = cfg;
    const g = keepG(towerGeo.clone());
    const im = new THREE.InstancedMesh(g, cfg.mat, count);
    const aUv  = new Float32Array(count * 4);
    const aSd  = new Float32Array(count);
    const d = new THREE.Object3D();
    for(let i = 0; i < count; i++){
      const a   = (i / count) * Math.PI * 2 + (rnd() - 0.5) * (Math.PI * 1.7 / count);
      const cs  = Math.cos(a), sn = Math.sin(a);
      const open = Math.max(0, Math.max(cs, sn));                       // 1 facing east/south, 0 in the blind corner
      let   r   = rMin + Math.pow(rnd(), 0.68) * (rMax - rMin);
      r *= 1 - 0.16 * open;
      const hero = rnd() > 0.94 ? 1.42 : 1;                             // a few that rise past our floor
      const h   = (hMin + Math.pow(rnd(), 1.5) * (hMax - hMin)) * (0.85 + 0.35 * open) * hero;
      const w   = wMin + rnd() * (wMax - wMin);
      const dep = w * (0.62 + rnd() * 0.7);
      d.position.set(cs * r, GROUND_Y + h / 2, sn * r);
      d.rotation.set(0, a + (rnd() - 0.5) * 0.9, 0);
      d.scale.set(w, h, dep);
      d.updateMatrix(); im.setMatrixAt(i, d.matrix);
      aUv[i * 4]     = rnd();
      aUv[i * 4 + 1] = rnd();
      aUv[i * 4 + 2] = w / TILE_W;                                      // window bays stay ~3.2m wide at any scale
      aUv[i * 4 + 3] = h / TILE_H;
      aSd[i] = rnd() * 97;
    }
    g.setAttribute('aUv',  new THREE.InstancedBufferAttribute(aUv, 4));
    g.setAttribute('aSeed', new THREE.InstancedBufferAttribute(aSd, 1));
    im.instanceMatrix.needsUpdate = true;
    im.castShadow = im.receiveShadow = false;   // far outside the host key's shadow camera
    im.computeBoundingSphere();
    group.add(im);
    return im;
  }
  const nearTowers = buildRing({ n:Q.near, r0:118, r1:205, h0:34, h1:132, w0:10, w1:26, mat:nearMat });
  const farTowers  = buildRing({ n:Q.far,  r0:195, r1:290, h0:26, h1:96,  w0:12, w1:30, mat:farMat });

  /* ---------- 5 · atmosphere: the haze band sitting on the city ---------- */
  let haze = null, hazeMat = null;
  if(Q.haze){
    const g = keepG(new THREE.CylinderGeometry(300, 322, 92, 48, 6, true));
    const pos = g.attributes.position, n = pos.count;
    const col = new Float32Array(n * 4);
    for(let i = 0; i < n; i++){
      const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
      const warm = Math.max(0, (x * SUN.x + z * SUN.z) / Math.hypot(x, z));   // 1 toward the sun, -1 away
      const t = warm * 0.5 + 0.5;
      col[i * 4] = t; col[i * 4 + 1] = t; col[i * 4 + 2] = t;                 // warm/cool weight, resolved in setTime
      const v = (y + 46) / 92;                                               // 0 at the base, 1 at the top
      col[i * 4 + 3] = Math.pow(1 - v, 1.8) * (0.35 + 0.65 * t);             // thickest low and sunward
    }
    g.setAttribute('color', new THREE.BufferAttribute(col, 4));
    hazeMat = keepM(new THREE.MeshBasicMaterial({
      vertexColors: true, transparent: true, opacity: 0.55, side: THREE.BackSide,
      blending: THREE.AdditiveBlending, depthWrite: false, fog: false,
    }));
    haze = new THREE.Mesh(g, hazeMat);
    haze.position.y = -18; haze.renderOrder = -8000; haze.frustumCulled = false;
    group.add(haze);
  }

  /* ---------- 6a · drifting cloud domes ---------- */
  const clouds = [];
  if(opts.clouds !== false && Q.clouds > 0){
    const cTex = keep(cloudTexture(rnd));
    const SPEC = [{ r:260, th:0.44, rep:3, spin:0.0042, op:1.0 },
                  { r:238, th:0.40, rep:2, spin:-0.0026, op:0.72 }];
    for(let i = 0; i < Q.clouds; i++){
      const s = SPEC[i];
      const t2 = keep(cTex.clone()); t2.needsUpdate = true;
      t2.wrapS = t2.wrapT = THREE.RepeatWrapping;
      t2.repeat.set(s.rep, s.rep);
      const m = keepM(new THREE.MeshBasicMaterial({
        map: t2, transparent: true, opacity: 0.34 * s.op, side: THREE.BackSide,
        depthWrite: false, fog: false,
      }));
      const g = keepG(new THREE.SphereGeometry(s.r, 32, 12, 0, Math.PI * 2, 0, Math.PI * s.th));
      const mesh = new THREE.Mesh(g, m);
      mesh.position.y = -s.r * 0.42;          // drop the cap so it meets the horizon instead of hovering
      mesh.renderOrder = -9000; mesh.frustumCulled = false;
      group.add(mesh);
      clouds.push({ mesh, mat: m, tex: t2, spin: s.spin, base: 0.34 * s.op });
    }
  }

  /* ---------- 7 · streets far below, with traffic ---------- */
  let groundGrp = null, groundMat = null; const traffic = [];
  if(opts.ground !== false && Q.ground){
    groundGrp = new THREE.Group(); groundGrp.position.y = GROUND_Y; group.add(groundGrp);
    const SIZE = 520, REP = 8;
    const sTex = keep(streetTexture(rnd)); sTex.repeat.set(REP, REP);
    groundMat = keepM(new THREE.MeshBasicMaterial({ map: sTex, fog: true, side: THREE.FrontSide }));
    const gp = keepG(new THREE.PlaneGeometry(SIZE, SIZE));
    const g0 = new THREE.Mesh(gp, groundMat);
    g0.rotation.x = -Math.PI / 2; g0.frustumCulled = false; groundGrp.add(g0);
    for(let i = 0; i < Q.traffic; i++){
      const axis = i === 0 ? 'x' : 'z';
      const tt = keep(trafficTexture(rnd, axis)); tt.repeat.set(REP, REP);
      const tm = keepM(new THREE.MeshBasicMaterial({
        map: tt, transparent: true, opacity: 0.3, blending: THREE.AdditiveBlending,
        depthWrite: false, fog: false, side: THREE.FrontSide,   // fog under additive brightens, it never hazes
      }));
      const q = new THREE.Mesh(keepG(new THREE.PlaneGeometry(SIZE, SIZE)), tm);
      q.rotation.x = -Math.PI / 2; q.position.y = 0.4; q.renderOrder = -7000;
      q.frustumCulled = false; groundGrp.add(q);
      traffic.push({ mat: tm, tex: tt, axis, speed: axis === 'x' ? 0.028 : -0.021 });
    }
    groundGrp.userData.snap = SIZE / REP;      // slide in whole blocks so the grid never swims under the camera
  }

  /* ---------- 6b · life: aircraft nav lights + roof beacons, one Points ---------- */
  const PLANES = opts.aircraft === false ? 0 : 2, BEACONS = 8;
  const lifeN = PLANES * 3 + BEACONS;
  const lifePos = new Float32Array(lifeN * 3), lifeCol = new Float32Array(lifeN * 3);
  const lifeGeo = keepG(new THREE.BufferGeometry());
  lifeGeo.setAttribute('position', new THREE.BufferAttribute(lifePos, 3));
  lifeGeo.setAttribute('color', new THREE.BufferAttribute(lifeCol, 3));
  lifeGeo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 400);
  const lifeMat = keepM(new THREE.PointsMaterial({
    size: 4.2, sizeAttenuation: false, map: keep(dotTexture()), vertexColors: true,
    transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, fog: false,
  }));
  const life = new THREE.Points(lifeGeo, lifeMat);
  life.frustumCulled = false; group.add(life);
  const flights = [];
  for(let i = 0; i < PLANES; i++){
    const head = rnd() * Math.PI * 2;
    flights.push({ head, y: 155 + i * 52, span: 620, speed: 7.5 + rnd() * 4,
                   t: rnd() * 620, lat: (rnd() - 0.5) * 190, ph: rnd() * 7 });
  }
  const beacons = [];
  for(let i = 0; i < BEACONS; i++){
    const a = rnd() * Math.PI * 2, r = 125 + rnd() * 150;
    beacons.push({ x: Math.cos(a) * r, y: GROUND_Y + 70 + rnd() * 88, z: Math.sin(a) * r, ph: rnd() * 7 });
  }

  /* ---------- 4 · the building we are in ---------- */
  const CONC  = keepM(new THREE.MeshStandardMaterial({ color:0x2a2823, roughness:0.94, metalness:0.02 }));
  const STEEL = keepM(new THREE.MeshStandardMaterial({ color:0x3a3a40, roughness:0.42, metalness:0.55 }));

  /* floor-plate fascia + head and sill rails — one merged draw */
  let fascia = null;
  {
    const parts = [], b = (w, h, d, x, y, z) => {
      const g = new THREE.BoxGeometry(w, h, d); g.translate(x, y, z); parts.push(g);
    };
    const fw = FASCIA.x1 - FASCIA.x0, fd = FASCIA.z1 - FASCIA.z0;
    const fh = 2.5, fcy = SLAB_BOT - fh / 2;                            // hangs off the slab soffit: the floor below
    b(fw, fh, 0.5, (FASCIA.x0 + FASCIA.x1) / 2, fcy, FASCIA.z0);
    b(fw, fh, 0.5, (FASCIA.x0 + FASCIA.x1) / 2, fcy, FASCIA.z1);
    b(0.5, fh, fd, FASCIA.x0, fcy, (FASCIA.z0 + FASCIA.z1) / 2);
    b(0.5, fh, fd, FASCIA.x1, fcy, (FASCIA.z0 + FASCIA.z1) / 2);
    const gw = GLAZE.x1 - GLAZE.x0, gd = GLAZE.z1 - GLAZE.z0;
    [[0.1, SLAB_TOP + 0.06], [0.12, CEIL_Y - 0.1]].forEach(([th, y]) => {   // sill and head rails
      b(gw, th, 0.2, (GLAZE.x0 + GLAZE.x1) / 2, y, GLAZE.z0);
      b(gw, th, 0.2, (GLAZE.x0 + GLAZE.x1) / 2, y, GLAZE.z1);
      b(0.2, th, gd, GLAZE.x0, y, (GLAZE.z0 + GLAZE.z1) / 2);
      b(0.2, th, gd, GLAZE.x1, y, (GLAZE.z0 + GLAZE.z1) / 2);
    });
    const merged = keepG(mergeGeometries(parts, false));
    parts.forEach(p => p.dispose());
    fascia = new THREE.Mesh(merged, CONC);
    fascia.castShadow = false;                     // it sits inside the host key's shadow map; casting blacks the plate
    fascia.receiveShadow = true;
    group.add(fascia);
  }

  /* perimeter columns — one InstancedMesh, clear of every room's furniture */
  {
    const pts = [];
    const push = (x, z) => pts.push([x, z]);
    const nx = Math.max(2, Math.round((GLAZE.x1 - GLAZE.x0) / 6.2));
    const nz = Math.max(2, Math.round((GLAZE.z1 - GLAZE.z0) / 6.2));
    for(let i = 0; i <= nx; i++){
      const x = GLAZE.x0 + (GLAZE.x1 - GLAZE.x0) * (i / nx);
      push(x, GLAZE.z0); push(x, GLAZE.z1);
    }
    for(let i = 1; i < nz; i++){
      const z = GLAZE.z0 + (GLAZE.z1 - GLAZE.z0) * (i / nz);
      push(GLAZE.x0, z); push(GLAZE.x1, z);
    }
    const g = keepG(new THREE.BoxGeometry(0.55, CEIL_Y, 0.55));
    const im = new THREE.InstancedMesh(g, CONC, pts.length);
    const d = new THREE.Object3D();
    pts.forEach((p, i) => {
      d.position.set(p[0], CEIL_Y / 2, p[1]); d.rotation.set(0, 0, 0); d.scale.set(1, 1, 1);
      d.updateMatrix(); im.setMatrixAt(i, d.matrix);
    });
    im.instanceMatrix.needsUpdate = true;
    im.castShadow = true; im.receiveShadow = true;      // perimeter columns throwing inward is the whole point
    im.computeBoundingSphere();
    group.add(im);
  }

  /* curtain-wall glass — one merged draw, faded out when you look straight down */
  const glassMat = keepM(new THREE.MeshStandardMaterial({
    color: 0x8ea3ad, roughness: 0.07, metalness: 0.12, transparent: true, opacity: 0.1,
    depthWrite: false, side: THREE.DoubleSide,
  }));
  const GLASS_OP = 0.1;
  {
    const gh = CEIL_Y - 0.28, gy = SLAB_TOP + 0.12 + gh / 2, parts = [];
    const pane = (w, x, z, ry) => {
      const g = new THREE.PlaneGeometry(w, gh);
      g.rotateY(ry); g.translate(x, gy, z); parts.push(g);
    };
    const gw = GLAZE.x1 - GLAZE.x0, gd = GLAZE.z1 - GLAZE.z0;
    pane(gw, (GLAZE.x0 + GLAZE.x1) / 2, GLAZE.z1, 0);
    pane(gw, (GLAZE.x0 + GLAZE.x1) / 2, GLAZE.z0, 0);
    pane(gd, GLAZE.x1, (GLAZE.z0 + GLAZE.z1) / 2, Math.PI / 2);
    pane(gd, GLAZE.x0, (GLAZE.z0 + GLAZE.z1) / 2, Math.PI / 2);
    const merged = keepG(mergeGeometries(parts, false));
    parts.forEach(p => p.dispose());
    const glass = new THREE.Mesh(merged, glassMat);
    glass.castShadow = glass.receiveShadow = false;    // a shadow-casting glass wall greys the whole plan
    glass.renderOrder = 6;
    group.add(glass);
  }

  /* mullions — thin vertical fins, one InstancedMesh, no shadow (108 thin casters alias) */
  {
    const pts = [], BAY = 1.55;
    const runX = GLAZE.x1 - GLAZE.x0, runZ = GLAZE.z1 - GLAZE.z0;
    const cx = Math.round(runX / BAY), cz = Math.round(runZ / BAY);
    for(let i = 0; i <= cx; i++){
      const x = GLAZE.x0 + runX * (i / cx);
      pts.push([x, GLAZE.z0, 0], [x, GLAZE.z1, 0]);
    }
    for(let i = 0; i <= cz; i++){
      const z = GLAZE.z0 + runZ * (i / cz);
      pts.push([GLAZE.x0, z, Math.PI / 2], [GLAZE.x1, z, Math.PI / 2]);
    }
    const g = keepG(new THREE.BoxGeometry(0.07, CEIL_Y - 0.2, 0.16));
    const im = new THREE.InstancedMesh(g, STEEL, pts.length);
    const d = new THREE.Object3D();
    pts.forEach((p, i) => {
      d.position.set(p[0], SLAB_TOP + 0.1 + (CEIL_Y - 0.2) / 2, p[1]);
      d.rotation.set(0, p[2], 0); d.scale.set(1, 1, 1);
      d.updateMatrix(); im.setMatrixAt(i, d.matrix);
    });
    im.instanceMatrix.needsUpdate = true;
    im.castShadow = false; im.receiveShadow = true;
    im.computeBoundingSphere();
    group.add(im);
  }

  /* ceiling slab + recessed downlights — OFF by default; the dollhouse view is the product */
  const ceiling = new THREE.Group();
  ceiling.visible = !!opts.ceiling;
  group.add(ceiling);
  {
    const w = GLAZE.x1 - GLAZE.x0 + 1.2, d = GLAZE.z1 - GLAZE.z0 + 1.2;
    const slab = new THREE.Mesh(keepG(new THREE.BoxGeometry(w, 0.34, d)), CONC);
    slab.position.set((GLAZE.x0 + GLAZE.x1) / 2, CEIL_Y + 0.17, (GLAZE.z0 + GLAZE.z1) / 2);
    slab.castShadow = false; slab.receiveShadow = true;    // casting from the ceiling blacks out the plate
    ceiling.add(slab);
    const lm = keepM(new THREE.MeshBasicMaterial({ color:0xffd9a8, fog:false, toneMapped:true }));
    lm.color.multiplyScalar(1.6);                          // linear, just over the host's 0.96 bloom toe
    const lg = keepG(new THREE.PlaneGeometry(0.62, 0.62));
    const cols = 10, rows = 5, im = new THREE.InstancedMesh(lg, lm, cols * rows);
    const o = new THREE.Object3D();
    let k = 0;
    for(let i = 0; i < cols; i++) for(let j = 0; j < rows; j++){
      o.position.set(GLAZE.x0 + (GLAZE.x1 - GLAZE.x0) * ((i + 0.5) / cols), CEIL_Y - 0.015,
                     GLAZE.z0 + (GLAZE.z1 - GLAZE.z0) * ((j + 0.5) / rows));
      o.rotation.set(Math.PI / 2, 0, 0); o.scale.set(1, 1, 1);
      o.updateMatrix(); im.setMatrixAt(k++, o.matrix);
    }
    im.instanceMatrix.needsUpdate = true;
    im.castShadow = im.receiveShadow = false;
    im.computeBoundingSphere();
    ceiling.add(im);
  }

  /* ---------- host state we borrow ---------- */
  /* The host rewrites scene.fog.density every frame in applyGrade(), so the fog
     object is never replaced and density is never written. Only the colour, and
     only when asked for. */
  const fog = (opts.fog === true && scene.fog) ? scene.fog : null;
  const savedFogCol = fog ? fog.color.clone() : null;
  let savedFar = null, farCam = opts.camera || null;
  if(opts.far && farCam){ savedFar = farCam.far; farCam.far = opts.far; farCam.updateProjectionMatrix(); }

  /* ---------- setTime ---------- */
  const cA = new THREE.Color(), cB = new THREE.Color(), cT = new THREE.Color();
  const P = {};
  for(const k in GRADE){ P[k] = [new THREE.Color(GRADE[k][0]), new THREE.Color(GRADE[k][1])]; }
  const lerp = (a, b, k) => a + (b - a) * k;
  let timeK = 0.34;

  const g = (name, out) => { out.copy(P[name][0]).lerp(P[name][1], timeK); return out; };
  const s = name => lerp(SCALAR[name][0], SCALAR[name][1], timeK);

  /* onBeforeCompile does not run until each material's FIRST RENDER, long after
     buildWorld returns — so the constructor's setTime() finds userData.u empty and
     the towers would render black with default window density forever. Every
     compile calls this, and so does every setTime. Hoisted: onBeforeCompile is a
     closure that only ever fires after this whole scope is initialised. */
  function applyTowerGrade(){
    /* the towers dissolve into exactly the colour the sky has at the horizon */
    g('horizon', cT); g('sodium', cA);
    cT.lerp(cA, 0.28 * s('sunGlow') + 0.06);
    [nearMat, farMat].forEach(m => {
      const u = m && m.userData.u; if(!u) return;
      g('towerTop', u.uTop.value);
      g('towerBot', u.uBot.value);
      u.uHaze.value.copy(cT);
      u.uLit.value  = s('winLit');
      u.uWinI.value = s('winInt');
    });
  }

  function setTime(k){
    timeK = Math.min(1, Math.max(0, k));

    g('zenith',  skyU.uZenith.value);
    g('midsky',  skyU.uMid.value);
    g('horizon', skyU.uHorizon.value);
    g('sodium',  skyU.uSodium.value);
    g('nadir',   skyU.uNadir.value);
    skyU.uGlow.value = s('sunGlow');

    applyTowerGrade();                          // cT is left holding the horizon colour
    if(fog) fog.color.copy(cT);                 // colour only — the host's GRADE owns density

    if(hazeMat){
      g('hazeWarm', cA); g('hazeCool', cB);
      hazeMat.color.copy(cB).lerp(cA, 0.62);      // vertex colour carries the sunward weight; this is the base tint
      hazeMat.opacity = s('hazeOp');
    }
    clouds.forEach(c => { g('cloud', c.mat.color); c.mat.opacity = (c.base / 0.34) * s('cloudOp'); });
    if(groundMat) g('street', groundMat.color);
    traffic.forEach(t => { t.mat.opacity = s('trafficOp'); });
  }
  setTime(opts.time !== undefined ? opts.time : 0.34);

  /* ---------- update ---------- */
  const fwd = new THREE.Vector3();
  let clock = 0;

  function update(dt, t, camera){
    const step = Math.min(Math.max(dt || 0, 0), 0.1);
    clock += step;
    const now = (t !== undefined ? t : clock);

    if(camera){
      const cx = camera.position.x, cy = camera.position.y, cz = camera.position.z;
      fwd.set(-camera.matrixWorld.elements[8], -camera.matrixWorld.elements[9], -camera.matrixWorld.elements[10]);

      sky.position.set(cx, cy, cz);
      if(haze) haze.position.set(cx, -18, cz);
      clouds.forEach(c => { c.mesh.position.x = cx; c.mesh.position.z = cz; });

      if(groundGrp){
        const q = groundGrp.userData.snap;
        groundGrp.position.x = Math.round(cx / q) * q;         // whole blocks only — otherwise the grid swims
        groundGrp.position.z = Math.round(cz / q) * q;
        groundGrp.visible = cy > GROUND_Y + 8 && !(cy > UNDER && fwd.y > 0.28);   // only when you can see down past the edge
      }
      /* glass clears out of the top-down plan and thickens as you drop to eye level */
      glassMat.opacity = GLASS_OP * (0.06 + 0.94 * THREE.MathUtils.smoothstep(fwd.y, -0.94, -0.5));
      /* mirror the host's own underside rule so the fascia does not hang in the void */
      const below = cy < UNDER;
      if(fascia.visible === below) fascia.visible = !below;
    }

    clouds.forEach(c => { c.mesh.rotation.y += c.spin * step; c.tex.offset.y += 0.0009 * step; });
    traffic.forEach(tr => {
      if(tr.axis === 'x') tr.tex.offset.x = (tr.tex.offset.x + tr.speed * step) % 1;
      else                tr.tex.offset.y = (tr.tex.offset.y + tr.speed * step) % 1;
    });

    [nearMat, farMat].forEach(m => { if(m.userData.u) m.userData.u.uTime.value = now; });

    /* aircraft: two slow crossings, port red / starboard green / a white strobe */
    let p = 0;
    flights.forEach(f => {
      f.t += f.speed * step;
      if(f.t > f.span) f.t -= f.span;
      const ch = Math.cos(f.head), sh = Math.sin(f.head);
      const d = f.t - f.span / 2;
      const x = ch * d - sh * f.lat, z = sh * d + ch * f.lat;
      const strobe = (Math.sin(now * 3.2 + f.ph) > 0.965) ? 3.4 : 0;
      const nav = 0.55 + 0.15 * Math.sin(now * 0.7 + f.ph);
      const set = (i, X, Y, Z, r, g2, b) => {
        lifePos[i * 3] = X; lifePos[i * 3 + 1] = Y; lifePos[i * 3 + 2] = Z;
        lifeCol[i * 3] = r; lifeCol[i * 3 + 1] = g2; lifeCol[i * 3 + 2] = b;
      };
      set(p++, x - sh * 5, f.y, z + ch * 5, nav * 1.2, nav * 0.1, nav * 0.08);   // port
      set(p++, x + sh * 5, f.y, z - ch * 5, nav * 0.1, nav * 1.1, nav * 0.25);   // starboard
      set(p++, x, f.y + 0.6, z, strobe, strobe, strobe);
    });
    beacons.forEach(b => {
      const on = Math.sin(now * 1.35 + b.ph) > 0.55 ? 1.9 : 0.06;
      lifePos[p * 3] = b.x; lifePos[p * 3 + 1] = b.y; lifePos[p * 3 + 2] = b.z;
      lifeCol[p * 3] = on; lifeCol[p * 3 + 1] = on * 0.16; lifeCol[p * 3 + 2] = on * 0.1;
      p++;
    });
    if(lifeN){ lifeGeo.attributes.position.needsUpdate = true; lifeGeo.attributes.color.needsUpdate = true; }
  }

  /* ---------- ceiling toggle ---------- */
  function setCeiling(on){ ceiling.visible = !!on; }

  /* ---------- dispose ---------- */
  function dispose(){
    if(fog && savedFogCol) fog.color.copy(savedFogCol);
    if(savedFar !== null && farCam){ farCam.far = savedFar; farCam.updateProjectionMatrix(); }
    group.removeFromParent();
    group.traverse(o => {
      if(o.isInstancedMesh && o.dispose) o.dispose();
      if(o.geometry) geo.push(o.geometry);
    });
    geo.forEach(g => g.dispose && g.dispose());
    mat.forEach(m => m.dispose && m.dispose());
    tex.forEach(t => t.dispose && t.dispose());
    geo.length = mat.length = tex.length = 0;
    group.clear();
  }

  return {
    group, update, setTime, setCeiling, dispose,
    /* live handles for tuning from the console */
    params: { get time(){ return timeK; }, sky: skyU, nearMat, farMat, fog,
              towers: { near: nearTowers, far: farTowers }, glassMat, ceiling, quality: opts.quality || 'high' },
  };
}
